// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

/**
 * API client for the funds-service admin surface (routed via the Admin BFF's
 * `funds-service` gateway route). Backs the Funds Retrieval History page.
 *
 * Run documents are proxied verbatim from the fund-aggregator's MongoDB
 * `runs` collection, so field names are snake_case; the enrichment fields
 * (lastSyncedRunId etc.) are camelCase because funds-service adds them.
 */

const API_BASE = '/api/funds/admin/refresh';

export type FundRunStatus = 'running' | 'completed' | 'partial_failure' | 'failed';
export type FundRunTrigger = 'scheduled' | 'manual' | 'startup';
export type ScraperStatus = 'success' | 'failed' | 'skipped';

export interface ScraperResult {
  scraper_id: string;
  name: string;
  status: ScraperStatus;
  funds_count: number;
  duration_seconds: number;
  exit_code?: number | null;
  error?: string | null;
}

export interface FundRun {
  run_id: string;
  started_at: string;
  finished_at: string | null;
  duration_seconds: number;
  trigger: FundRunTrigger;
  status: FundRunStatus;
  total_scrapers: number;
  scrapers_passed: number;
  scrapers_failed: number;
  total_funds: number;
  scrapers: ScraperResult[];
  /** Set by the aggregator's boot reconciliation on runs orphaned by a crash. */
  reconciled_on_boot?: boolean;
}

export interface RunHistoryResponse {
  runs: FundRun[];
  limit: number;
  skip: number;
  /** run_id of the last run funds-service actually synced into Postgres. */
  lastSyncedRunId?: string;
  lastSyncedAt?: string;
  lastSyncStatus?: string;
}

export interface AggregatorStatus {
  build?: { sha: string; started_at: string };
  last_run?: FundRun | null;
  next_run_time?: string | null;
  /** "unavailable" when funds-service's circuit-breaker fallback answered. */
  status?: string;
  error?: string;
}

async function throwApiError(response: Response, fallback: string): Promise<never> {
  if (response.status === 403) {
    throw new Error('Insufficient permissions to manage fund retrieval');
  }
  if (response.status === 429) {
    throw new Error('Refresh limit reached for this hour. Try again later.');
  }
  if (response.status === 503) {
    throw new Error('The fund aggregator is unreachable. Try again shortly.');
  }
  let message = fallback;
  try {
    const body = await response.json();
    if (body?.message) {
      message = body.message;
    }
  } catch {
    // Non-JSON error body — keep the fallback message.
  }
  throw new Error(message);
}

export const fundsApi = {
  /** Paginated run history, newest first (limit 1-100, skip >= 0). */
  async getRunHistory(limit: number, skip: number): Promise<RunHistoryResponse> {
    const params = new URLSearchParams({ limit: String(limit), skip: String(skip) });
    const response = await fetch(`${API_BASE}/runs?${params}`, {
      credentials: 'include',
    });
    if (!response.ok) {
      await throwApiError(response, 'Failed to load fund retrieval history');
    }
    return response.json();
  },

  /** Aggregator live status: last run summary + next scheduled run time. */
  async getStatus(): Promise<AggregatorStatus> {
    const response = await fetch(`${API_BASE}/status`, {
      credentials: 'include',
    });
    if (!response.ok) {
      await throwApiError(response, 'Failed to load aggregator status');
    }
    return response.json();
  },

  /** Trigger a manual retrieval run (rate-limited server-side per brokerage). */
  async triggerRefresh(): Promise<{ status: string; message?: string }> {
    const response = await fetch(API_BASE, {
      method: 'POST',
      credentials: 'include',
    });
    if (!response.ok) {
      await throwApiError(response, 'Failed to trigger a retrieval run');
    }
    return response.json();
  },
};
